import { useEffect, useRef, useState } from "react";
import { Package, Upload, CheckCircle2, AlertCircle, RefreshCw, Boxes, AlertTriangle, TrendingDown, Euro } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const fmtN = (n) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function AdminGiacenze() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const inputRef = useRef(null);
  const snapshotInputRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/giacenze/summary");
      setSummary(r.data);
    } catch (e) {
      toast.error("Errore caricamento giacenze");
    } finally { setLoading(false); }
  };

  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const r = await authAxios.get("/admin/forecast/stock-alerts?severity=all&weeks=4");
      setAlerts(r.data);
    } catch (e) {
      // se non c'è storico vendite, l'endpoint può restituire vuoto: silent
      setAlerts(null);
    } finally { setAlertsLoading(false); }
  };

  useEffect(() => { refresh(); loadAlerts(); }, []);

  const handleUpload = async (e, opts = {}) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Serve un file PDF");
      return;
    }
    const { asSnapshot = false, snapshotLabel = "" } = opts;
    setUploading(true);
    const tid = toast.loading(asSnapshot ? "Salvataggio snapshot..." : "Avvio elaborazione...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = asSnapshot
        ? `?as_snapshot=true&snapshot_label=${encodeURIComponent(snapshotLabel || `Snapshot ${new Date().toISOString().slice(0, 10)}`)}`
        : "";
      const startResp = await authAxios.post(`/giacenze/upload${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      const jobId = startResp.data?.job_id;
      if (!jobId) throw new Error("Risposta server non valida");

      let attempts = 0;
      let finalJob = null;
      while (attempts < 300) {
        await new Promise((r) => setTimeout(r, 2000));
        attempts += 1;
        try {
          const s = await authAxios.get(`/giacenze/upload-status/${jobId}`, { timeout: 30000 });
          const job = s.data;
          if (job.status === "processing") {
            const pc = job.progress_current || 0;
            const pt = job.progress_total || 0;
            toast.loading(pt > 0 ? `Elaborazione: pagina ${pc}/${pt}` : "Elaborazione PDF in corso...", { id: tid });
            continue;
          }
          if (job.status === "done" || job.status === "failed") { finalJob = job; break; }
        } catch { /* network blip, keep polling */ }
      }
      if (!finalJob) {
        toast.error("Timeout elaborazione (>10 min)", { id: tid });
      } else if (finalJob.status === "failed") {
        toast.error(finalJob.error || "Elaborazione fallita", { id: tid, duration: 8000 });
      } else {
        const r = finalJob.result || {};
        toast.success(r.message || (asSnapshot ? "Snapshot salvato" : "Giacenze aggiornate"), { id: tid, duration: 4000 });
        await refresh();
        await loadAlerts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Errore caricamento", { id: tid });
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const fmtDT = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-giacenze-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
        <PageHeader title="Giacenze" />
        <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-white">Giacenze magazzino</h1>
            <p className="text-zinc-400 text-xs sm:text-sm">Carica il PDF &quot;Giacenze per magazzino&quot; del gestionale</p>
          </div>
          <Button onClick={refresh} variant="ghost" size="sm" disabled={loading} className="text-zinc-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        </div>

        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2"><Boxes className="w-4 h-4 text-emerald-400" /> Stato attuale</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-zinc-300">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-[#0f0f11] rounded-lg p-3 border border-white/5">
                <p className="text-zinc-400 text-[11px] uppercase tracking-wider">Articoli</p>
                <p className="text-2xl font-mono font-bold text-white mt-0.5" data-testid="total-skus">{summary?.total ?? 0}</p>
              </div>
              <div className="bg-[#0f0f11] rounded-lg p-3 border border-emerald-500/20">
                <p className="text-emerald-300/80 text-[11px] uppercase tracking-wider">Disponibili</p>
                <p className="text-2xl font-mono font-bold text-emerald-400 mt-0.5">{summary?.positive ?? 0}</p>
              </div>
              <div className="bg-[#0f0f11] rounded-lg p-3 border border-red-500/20">
                <p className="text-red-300/80 text-[11px] uppercase tracking-wider">Esauriti</p>
                <p className="text-2xl font-mono font-bold text-red-400 mt-0.5">{summary?.non_positive ?? 0}</p>
              </div>
            </div>

            {/* Valore magazzino al prezzo d'acquisto (giacenza × price_purchase) */}
            <div
              className="bg-gradient-to-br from-amber-500/10 via-[#0f0f11] to-[#0f0f11] rounded-lg p-3 border border-amber-500/25"
              data-testid="warehouse-value-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-amber-300/80 text-[11px] uppercase tracking-wider flex items-center gap-1.5">
                    <Euro className="w-3 h-3" />
                    Valore magazzino all&apos;acquisto
                  </p>
                  <p
                    className="text-2xl sm:text-3xl font-mono font-bold text-amber-300 mt-1"
                    data-testid="warehouse-value-total"
                    title={`${Number(summary?.value_purchase || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" })} · costo d'acquisto × giacenza`}
                  >
                    {fmtEur(summary?.value_purchase)}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    {fmtN(summary?.n_with_cost)} articoli con costo
                  </p>
                </div>
                <div className="text-right shrink-0 border-l border-amber-500/20 pl-3">
                  <p className="text-amber-300/60 text-[10px] uppercase tracking-wider">Totale pz/kg</p>
                  <p
                    className="text-xl sm:text-2xl font-mono font-bold text-amber-200 mt-1"
                    data-testid="warehouse-qty-total"
                  >
                    {fmtN(summary?.qty_total)}
                  </p>
                  {summary?.n_missing_cost > 0 && (
                    <p className="text-[10px] text-amber-400/70 mt-0.5" title="Pezzi/kg esclusi dal calcolo del valore perché senza costo">
                      di cui {fmtN(summary.qty_missing_cost)} sc.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Valore magazzino a prezzo MEDIO DI VENDITA anno solare (potenziale ricavo) */}
            <div
              className="bg-gradient-to-br from-emerald-500/10 via-[#0f0f11] to-[#0f0f11] rounded-lg p-3 border border-emerald-500/25"
              data-testid="warehouse-avg-value-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-emerald-300/80 text-[11px] uppercase tracking-wider flex items-center gap-1.5">
                    <Euro className="w-3 h-3" />
                    Valore magazzino a prezzo medio vendita {summary?.avg_price_year || ""}
                  </p>
                  <p
                    className="text-2xl sm:text-3xl font-mono font-bold text-emerald-300 mt-1"
                    data-testid="warehouse-avg-value-total"
                    title="Somma qty × prezzo medio di vendita ricavato dallo storico dell&apos;anno solare in corso (eur_tot / qta_tot per SKU su sales_rows)"
                  >
                    {fmtEur(summary?.value_avg_selling)}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Margine teorico:{" "}
                    <span className="text-emerald-400 font-semibold">
                      {fmtEur((summary?.value_avg_selling || 0) - (summary?.value_purchase || 0))}
                    </span>
                    {summary?.value_purchase > 0 && (
                      <span className="text-zinc-500">
                        {" "}(+{Math.round(((summary?.value_avg_selling || 0) - (summary?.value_purchase || 0)) / (summary?.value_purchase || 1) * 100)}%)
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right shrink-0 border-l border-emerald-500/20 pl-3">
                  <p className="text-emerald-300/60 text-[10px] uppercase tracking-wider">Articoli venduti</p>
                  <p className="text-xl sm:text-2xl font-mono font-bold text-emerald-200 mt-1">
                    {fmtN(summary?.n_with_avg)}
                  </p>
                  {summary?.n_missing_avg > 0 && (
                    <p className="text-[10px] text-emerald-400/70 mt-0.5" title="Articoli mai venduti nell'anno corrente, esclusi dal calcolo del valore a prezzo medio">
                      +{fmtN(summary.n_missing_avg)} non venduti
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Confronto con snapshot di inizio anno (se presente) */}
            {summary?.snapshot && (
              <div
                className="bg-gradient-to-br from-sky-500/10 via-[#0f0f11] to-[#0f0f11] rounded-lg p-3 border border-sky-500/25"
                data-testid="warehouse-snapshot-compare-card"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sky-300/80 text-[11px] uppercase tracking-wider">
                    Confronto con {summary.snapshot.label}
                  </p>
                  <span className="text-[10px] text-zinc-500 font-mono">
                    {summary.snapshot.count} art. · {fmtN(summary.snapshot.qty_total)} pz/kg
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#0f0f11] rounded p-2 border border-white/5">
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Δ Pz/Kg totali</p>
                    <p
                      className={`text-lg font-mono font-bold mt-0.5 ${((summary?.qty_total || 0) - (summary.snapshot.qty_total || 0)) >= 0 ? "text-emerald-300" : "text-red-300"}`}
                      data-testid="snapshot-delta-qty"
                    >
                      {((summary?.qty_total || 0) - (summary.snapshot.qty_total || 0)) >= 0 ? "+" : ""}
                      {fmtN((summary?.qty_total || 0) - (summary.snapshot.qty_total || 0))}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      {fmtN(summary.snapshot.qty_total)} → {fmtN(summary?.qty_total)}
                    </p>
                  </div>
                  <div className="bg-[#0f0f11] rounded p-2 border border-white/5">
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Δ Valore all&apos;acquisto</p>
                    <p
                      className={`text-lg font-mono font-bold mt-0.5 ${summary.snapshot.delta_purchase >= 0 ? "text-emerald-300" : "text-red-300"}`}
                      data-testid="snapshot-delta-purchase"
                    >
                      {summary.snapshot.delta_purchase >= 0 ? "+" : ""}{fmtEur(summary.snapshot.delta_purchase)}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      {fmtEur(summary.snapshot.value_purchase)} → {fmtEur(summary?.value_purchase)}
                    </p>
                  </div>
                  <div className="bg-[#0f0f11] rounded p-2 border border-white/5">
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Δ Valore vendita medio</p>
                    <p
                      className={`text-lg font-mono font-bold mt-0.5 ${summary.snapshot.delta_avg_selling >= 0 ? "text-emerald-300" : "text-red-300"}`}
                      data-testid="snapshot-delta-avg"
                    >
                      {summary.snapshot.delta_avg_selling >= 0 ? "+" : ""}{fmtEur(summary.snapshot.delta_avg_selling)}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      {fmtEur(summary.snapshot.value_avg_selling)} → {fmtEur(summary?.value_avg_selling)}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex justify-between text-[10px] text-zinc-500">
                  <span>Salvato il {fmtDT(summary.snapshot.ts)}</span>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(`Eliminare lo snapshot "${summary.snapshot.label}"?`)) return;
                      try {
                        await authAxios.delete(`/giacenze/snapshots/${summary.snapshot.id}`);
                        toast.success("Snapshot eliminato");
                        await refresh();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Errore eliminazione");
                      }
                    }}
                    className="text-red-400 hover:text-red-300 underline"
                    data-testid="snapshot-delete-btn"
                  >
                    Elimina
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-between pt-2">
              <span className="text-zinc-400">Ultimo aggiornamento</span>
              <span className="text-zinc-200">{fmtDT(summary?.last_upload?.ts)}</span>
            </div>
            {summary?.last_upload && (
              <div className="flex justify-between">
                <span className="text-zinc-400">Origine</span>
                <Badge className={summary.last_upload.source === "dropbox" ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : "bg-zinc-500/15 text-zinc-300 border-zinc-500/30"}>
                  {summary.last_upload.source === "dropbox" ? "Dropbox auto" : "Upload manuale"}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stock alerts (forecast vs giacenza) */}
        {alerts && alerts.total_alerts > 0 && (
          <Card className="bg-red-500/5 border-red-500/30 mb-4" data-testid="stock-alerts-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  Alert Stock-Out
                </span>
                <div className="flex gap-1.5 text-[10px]">
                  {alerts.counts.esaurito > 0 && (
                    <Badge variant="outline" className="border-red-500/40 text-red-300 bg-red-500/10">
                      {alerts.counts.esaurito} esauriti
                    </Badge>
                  )}
                  {alerts.counts.basso > 0 && (
                    <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10">
                      {alerts.counts.basso} bassi
                    </Badge>
                  )}
                  {alerts.counts.attenzione > 0 && (
                    <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10">
                      {alerts.counts.attenzione} attenzione
                    </Badge>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[11px] text-zinc-400 mb-2">
                Articoli dove la giacenza attuale è inferiore alla previsione settimanale (cliccabili → dettaglio).
              </p>
              <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="stock-alerts-list">
                {alerts.items.slice(0, 30).map((it, i) => (
                  <button
                    key={it.sku}
                    type="button"
                    onClick={() => navigate("/admin/forecast")}
                    className="w-full flex items-center justify-between p-2 rounded hover:bg-white/5 border border-transparent hover:border-white/10 text-left"
                    data-testid={`stock-alert-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {it.stock_alert === "esaurito" ? (
                          <TrendingDown className="w-3.5 h-3.5 text-red-400 shrink-0" />
                        ) : (
                          <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${
                            it.stock_alert === "basso" ? "text-orange-400" : "text-amber-400"
                          }`} />
                        )}
                        <Badge variant="outline" className="text-[10px] text-zinc-400 border-white/10">{it.sku}</Badge>
                        <span className="text-sm text-white truncate">{it.descr}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2 text-[11px]">
                      <div className={
                        it.stock_alert === "esaurito" ? "text-red-300 font-semibold" :
                        it.stock_alert === "basso" ? "text-orange-300" : "text-amber-300"
                      }>
                        stock {fmtN(it.giacenza_attuale)}
                      </div>
                      <div className="text-zinc-500">
                        prev. {fmtN(it.forecast_next_week)}/sett.
                      </div>
                    </div>
                  </button>
                ))}
                {alerts.items.length > 30 && (
                  <div className="text-[11px] text-zinc-500 text-center pt-2">
                    + altri {alerts.items.length - 30} articoli a rischio — vedi <button type="button" onClick={() => navigate("/admin/forecast")} className="underline text-amber-300">Previsione Domanda</button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#18181b] border-white/5 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2"><Upload className="w-4 h-4 text-blue-400" /> Carica PDF</CardTitle>
          </CardHeader>
          <CardContent>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleUpload}
              className="hidden"
              data-testid="giacenze-file-input"
            />
            <input
              ref={snapshotInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => {
                const defaultLabel = `Inizio ${new Date().getFullYear()}`;
                const label = window.prompt("Etichetta per lo snapshot:", defaultLabel);
                if (label === null) {
                  if (e.target) e.target.value = "";
                  return;
                }
                handleUpload(e, { asSnapshot: true, snapshotLabel: label.trim() || defaultLabel });
              }}
              className="hidden"
              data-testid="giacenze-snapshot-file-input"
            />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="upload-btn">
                <Upload className={`w-4 h-4 mr-1.5 ${uploading ? "animate-spin" : ""}`} />
                {uploading ? "Caricamento..." : "Carica PDF Giacenze"}
              </Button>
              <Button
                onClick={() => snapshotInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                data-testid="upload-snapshot-btn"
                title="Carica un PDF e salvalo come snapshot storico (es. inizio anno) senza sovrascrivere le giacenze correnti"
              >
                <Boxes className="w-4 h-4 mr-1.5" />
                Salva come snapshot
              </Button>
            </div>
            <p className="text-zinc-500 text-[11px] mt-2 leading-snug">
              Suggerimento: per aggiornamenti automatici, deposita il PDF nella cartella Dropbox <span className="font-mono text-zinc-300">/Triolo.Store-Sync/Giacenze/</span>. Verrà importato entro 10 minuti.
              <br />
              <span className="text-sky-300/80">Snapshot</span>: usa &quot;Salva come snapshot&quot; per fotografare il magazzino a una data (es. 1° gennaio) e confrontarlo poi con lo stato attuale.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4 text-xs text-amber-200/80 leading-relaxed flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-200 mb-1">Note importanti</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Ogni nuovo upload <strong>sostituisce</strong> completamente le giacenze precedenti (snapshot momentaneo).</li>
                <li>L&apos;app non blocca gli ordini se la quantità richiesta supera la giacenza: il valore è solo informativo.</li>
                <li>Quantità negative o zero appaiono in rosso nei Giri e nell&apos;editor ordini.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
